import nanoid from 'nanoid';
import { InferType } from 'yup';
import { Yup, disableValidationIfEntityDestoyed, validateFile } from 'src/helpers';

export const getGeneralFormSchema = applicationFormSettings =>
  Yup.object({
    first_name: Yup.string()
      .required()
      .nullable()
      .default(''),
    last_name: Yup.string()
      .required()
      .nullable()
      .default(''),
    email: Yup.string()
      .customEmail()
      .required()
      .nullable()
      .default(''),
    phone: applicationFormSettings?.phone
      ? Yup.string()
        .required()
        .nullable()
        .default('')
      : Yup.mixed(),
    country: applicationFormSettings?.country
      ? Yup.string()
        .required()
        .nullable()
        .default('')
      : Yup.mixed()
  });

const baseExperienceValidationSchema = Yup.object({
  title: Yup.string()
    .nullable()
    .label('company')
    .required(),
  position_title: Yup.string()
    .nullable()
    .label('position title')
    .required(),
  startDate: Yup.object({
    month: Yup.string()
      .label('Month')
      .required(),
    year: Yup.string()
      .label('Year')
      .required()
  }),
  endDate: Yup.object({
    month: Yup.string()
      .label('Month')
      .required(),
    year: Yup.string()
      .label('Year')
      .when('month', (month, schema) => {
        return month === 'Current' ? schema : schema.required();
      })
  }).isLater('startDate'),
  industries: Yup.array()
    .of(Yup.string())
    .label('industries')
    .nullable()
    .required()
    .default([]),
  job_functions: Yup.array()
    .of(Yup.string())
    .label('job functions')
    .nullable()
    .required()
    .default([]),
  main_theme: Yup.object()
    .label('main area')
    .nullable()
    .required(),
  sub_theme: Yup.object()
    .label('activity')
    .nullable()
    .required()
    .default([]),
  skills_attributes: Yup.array()
    .of(
      Yup.object({
        name: Yup.string(),
        level: Yup.string(),
        experience: Yup.number()
          .typeError(' ')
          .min(0, ' ')
          .max(100, ' ')
          .nullable()
      }).required()
    )
    .default([])
}).nullable();

export const getDefaultExperience = () => ({
  cid: nanoid(),
  ...baseExperienceValidationSchema.default()
});

const experienceSchema = disableValidationIfEntityDestoyed(baseExperienceValidationSchema);

export const getExperiencesFormSchema = applicationFormSettings =>
  applicationFormSettings?.professional_experience &&
  Yup.object({
    experiences_attributes: Yup.array()
      .of(experienceSchema)
      .default([getDefaultExperience()])
      .required()
  });

const baseEducationSchema = Yup.object({
  university: Yup.string()
    .label('institution')
    .nullable()
    .required(),
  degree: Yup.string()
    .label('degree')
    .nullable()
    .required(),
  major: Yup.string()
    .label('field of study')
    .nullable()
    .required(),
  startDate: Yup.object({
    month: Yup.string()
      .label('Month')
      .required(),
    year: Yup.string()
      .label('Year')
      .required()
  }),
  endDate: Yup.object({
    month: Yup.string()
      .label('Month')
      .required(),
    year: Yup.string()
      .label('Year')
      .required()
  }).isLater('startDate')
}).nullable();

export const getDefaultEducation = () => ({
  cid: nanoid(),
  ...baseEducationSchema.default()
});

const educationSchema = disableValidationIfEntityDestoyed(baseEducationSchema);

export const getEducationsFormSchema = applicationFormSettings =>
  applicationFormSettings?.education &&
  Yup.object({
    degrees_attributes: Yup.array()
      .of(educationSchema)
      .default([getDefaultEducation()])
      .required()
  });

export const allowedResumeExtensions = ['pdf'];

export const COVER_LETTER_MAX_WORDS = 1000;

const languageSchema = Yup.object({
  name: Yup.string()
    .label('Language')
    .required(),
  level: Yup.string()
    .label('Level')
    .required()
});

export const getDefaultLanguageValue = () => ({
  cid: nanoid(),
  ...languageSchema.default()
});

export const getCvFormSchema = applicationFormSettings =>
  Yup.object({
    gender: Yup.string()
      .nullable()
      .when('preferNotToSayGender', (preferNotToSayGender, schema) =>
        preferNotToSayGender ? schema : schema.required().nullable()
      ),
    preferNotToSayGender: Yup.boolean(),
    is_first_internship: Yup.string()
      .nullable()
      .default('false'),
    languages_attributes: applicationFormSettings?.languages
      ? Yup.array()
        .of(languageSchema)
        .min(1)
        .default([getDefaultLanguageValue()])
        .required()
      : Yup.mixed(),
    linkedin: Yup.string()
      .url()
      .nullable(),
    resume_filename: Yup.string().nullable(),
    cv_file: Yup.mixed()
      .oneOfFileExtension(allowedResumeExtensions)
      .when('resume_filename', (resume_filename, schema) => (resume_filename ? schema : schema.required())),
    cover_letter: applicationFormSettings?.cover_letter
      ? Yup.string()
        .label('Cover letter')
        .maxWords(COVER_LETTER_MAX_WORDS)
        .required()
        .nullable()
      : Yup.mixed(),
    custom_answers: validateFile()
  });

export const getValidationSchema = applicationFormSettings =>
  getGeneralFormSchema(applicationFormSettings)
    .concat(getExperiencesFormSchema(applicationFormSettings))
    .concat(getEducationsFormSchema(applicationFormSettings))
    .concat(getCvFormSchema(applicationFormSettings));

export type ValuesType = InferType<ReturnType<typeof getValidationSchema>>;
